import React, { useState } from "react";
import { StorageService } from "../../services/storage.js";
import { Database, Play, Terminal, Table, RefreshCw, CheckCircle2, AlertCircle, Download, FileCode } from "lucide-react";

export const AdminDatabase = () => {
  const [query, setQuery] = useState("SELECT * FROM submissions WHERE status = 'new';");
  const [queryResult, setQueryResult] = useState(() => {
    return StorageService.executeSql("SELECT * FROM submissions WHERE status = 'new';");
  });
  const [selectedTable, setSelectedTable] = useState("submissions");
  const [resetMessage, setResetMessage] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const tables = ["users", "roles", "pages", "teachers", "posts", "curriculum", "submissions", "settings"];
  const sampleQueries = [
    { label: "New Submissions", sql: "SELECT * FROM submissions WHERE status = 'new';" },
    { label: "Published Posts", sql: "SELECT * FROM posts WHERE status = 'published';" },
    { label: "Active Faculty", sql: "SELECT * FROM teachers WHERE is_active = 1;" },
    { label: "Active Curriculum", sql: "SELECT * FROM curriculum WHERE is_active = 1;" },
    { label: "Admin Users", sql: "SELECT * FROM users WHERE role = 'super_admin';" },
    { label: "Site Settings", sql: "SELECT * FROM settings;" },
    { label: "Show Tables", sql: "SHOW TABLES;" },
    { label: "Describe Teachers", sql: "DESCRIBE teachers;" }
  ];

  const handleRunQuery = (sqlToRun) => {
    const targetSql = sqlToRun || query;
    const res = StorageService.executeSql(targetSql);
    setQueryResult(res);
  };

  const handleQuickSelectTable = (tbl) => {
    setSelectedTable(tbl);
    const sql = `SELECT * FROM ${tbl};`;
    setQuery(sql);
    handleRunQuery(sql);
  };

  const handleResetSeed = () => {
    if (window.confirm("Reset database tables to default factory seed data?")) {
      StorageService.resetDatabase();
      setResetMessage(true);
      handleRunQuery();
      setTimeout(() => setResetMessage(false), 3000);
    }
  };

  const handleDownloadSql = () => {
    const sqlContent = StorageService.generateSqlDump();
    const blob = new Blob([sqlContent], { type: "application/sql" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `baitul_database_dump_${Date.now()}.sql`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setDownloadSuccess(true);
    setTimeout(() => setDownloadSuccess(false), 4000);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-serif font-bold text-slate-900 flex items-center gap-2">
            <Database className="w-6 h-6 text-emerald-800" />
            <span>MySQL Relational Database Studio</span>
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Production MySQL engine & relational schema for <code className="bg-stone-100 px-1.5 py-0.5 rounded text-emerald-900 font-mono font-bold">baitul</code> (User: <code className="font-mono text-emerald-800 font-bold">root</code>, Password: <code className="font-mono text-slate-600 font-bold">none</code>)
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
          <button
            onClick={handleDownloadSql}
            className="px-3.5 py-1.5 text-xs font-bold text-white bg-emerald-800 hover:bg-emerald-900 rounded-lg shadow-xs flex items-center gap-1.5 cursor-pointer"
          >
            <Download className="w-3.5 h-3.5" />
            <span>Export baitul.sql</span>
          </button>

          <button
            onClick={handleResetSeed}
            className="px-3.5 py-1.5 text-xs font-semibold text-slate-700 bg-white hover:bg-stone-50 border border-stone-200 rounded-lg shadow-xs flex items-center gap-1.5 cursor-pointer"
          >
            <RefreshCw className="w-3.5 h-3.5 text-amber-600" />
            <span>Reset Seed</span>
          </button>
        </div>
      </div>

      {downloadSuccess && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl text-xs font-semibold flex items-center gap-2 animate-in fade-in">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <span>Production SQL dump exported successfully! (Database: <code>baitul</code>, User: <code>root</code>)</span>
        </div>
      )}

      {resetMessage && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl text-xs flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <span>Database successfully reset and re-seeded with relational madrasah records!</span>
        </div>
      )}

      {/* Production Credentials Banner */}
      <div className="p-4 bg-emerald-950 text-white rounded-2xl border border-emerald-900 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm">
        <div className="space-y-1 text-xs">
          <span className="text-amber-300 font-bold uppercase tracking-wider text-[10px] block">MySQL Production Configuration</span>
          <div className="flex flex-wrap items-center gap-4 font-mono text-[11px] text-emerald-100">
            <span>Host: <strong className="text-white">localhost</strong></span>
            <span>·</span>
            <span>Database: <strong className="text-amber-300">baitul</strong></span>
            <span>·</span>
            <span>Username: <strong className="text-white">root</strong></span>
            <span>·</span>
            <span>Password: <strong className="text-slate-300">(none / empty)</strong></span>
            <span>·</span>
            <span>Port: <strong className="text-white">3306</strong></span>
          </div>
        </div>

        <button
          onClick={handleDownloadSql}
          className="px-3 py-1.5 bg-amber-400 hover:bg-amber-300 text-emerald-950 rounded-lg text-xs font-bold font-sans flex items-center gap-1.5 cursor-pointer shrink-0"
        >
          <FileCode className="w-3.5 h-3.5" />
          <span>Download .SQL Dump</span>
        </button>
      </div>

      {/* Tables Bar */}
      <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-xs space-y-3">
        <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block">
          Relational Database Tables (InnoDB / UTF8mb4):
        </span>
        <div className="flex flex-wrap gap-2">
          {tables.map((tbl) => (
            <button
              key={tbl}
              onClick={() => handleQuickSelectTable(tbl)}
              className={`px-3 py-1.5 text-xs font-mono rounded-lg border transition-all cursor-pointer flex items-center gap-1.5 ${
                selectedTable === tbl
                  ? "bg-emerald-950 text-amber-300 border-emerald-950 shadow-xs font-bold"
                  : "bg-stone-50 text-slate-700 border-stone-200 hover:bg-stone-100"
              }`}
            >
              <Table className="w-3.5 h-3.5 text-emerald-600" />
              <span>{tbl}</span>
            </button>
          ))}
        </div>
      </div>

      {/* SQL Query Terminal */}
      <div className="bg-slate-900 rounded-2xl p-5 text-white shadow-md border border-slate-800 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Terminal className="w-4 h-4 text-emerald-400" />
            <span className="font-mono text-emerald-400 font-semibold">mysql &gt;</span>
            <span>Execute SQL Query</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => handleRunQuery()}
              className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer shadow-xs"
            >
              <Play className="w-3.5 h-3.5 fill-current" />
              <span>Run SQL</span>
            </button>
          </div>
        </div>

        <div className="relative font-mono text-xs">
          <textarea
            rows={3}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full bg-slate-950 text-amber-300 p-3 rounded-lg border border-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-500 font-mono text-xs leading-relaxed"
            placeholder="SELECT * FROM posts;"
          />
        </div>

        {/* Quick Query Templates */}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <span className="text-[11px] text-slate-400">Quick queries:</span>
          {sampleQueries.map((sq, i) => (
            <button
              key={i}
              onClick={() => {
                setQuery(sq.sql);
                handleRunQuery(sq.sql);
              }}
              className="text-[11px] text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 px-2.5 py-1 rounded font-mono transition-colors cursor-pointer"
            >
              {sq.label}
            </button>
          ))}
        </div>
      </div>

      {/* Query Execution Status */}
      {queryResult && (
        <div className="flex items-center justify-between text-xs text-slate-500 px-1 font-mono">
          <div className="flex items-center gap-2">
            {queryResult.success ? (
              <span className="text-emerald-700 font-semibold flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>{queryResult.message || "Query OK"}</span>
              </span>
            ) : (
              <span className="text-red-600 font-semibold flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                <span>{queryResult.message}</span>
              </span>
            )}
          </div>
          <span className="tabular-nums">Latency: {queryResult.executionTimeMs} ms</span>
        </div>
      )}

      {/* Results Table */}
      {queryResult && queryResult.rows && queryResult.rows.length > 0 && (
        <div className="bg-white rounded-2xl border border-stone-200 shadow-xs overflow-hidden">
          <div className="overflow-x-auto max-h-[500px]">
            <table className="w-full text-left text-xs">
              <thead className="bg-stone-50 border-b border-stone-200 text-slate-600 sticky top-0">
                <tr>
                  {queryResult.columns.map((col, idx) => (
                    <th key={idx} className="py-2.5 px-3.5 font-mono font-bold whitespace-nowrap text-slate-700">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 font-mono">
                {queryResult.rows.map((row, rIdx) => (
                  <tr key={rIdx} className="hover:bg-stone-50/70 transition-colors">
                    {queryResult.columns.map((col, cIdx) => {
                      const val = row[col];
                      const displayVal = typeof val === "object" ? JSON.stringify(val) : String(val ?? "NULL");
                      return (
                        <td key={cIdx} className="py-2.5 px-3.5 text-slate-800 whitespace-nowrap max-w-xs truncate">
                          {displayVal}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
